import { useEffect, useMemo, useState } from "react";
import { Sparkles } from "lucide-react";
import LightRays from "./LightRays";

type WelcomeLoaderProps = {
  durationMs?: number;
  onComplete?: () => void;
  title?: string;
};

const WelcomeLoader = ({
  durationMs = 5000,
  onComplete,
  title = "VaWeNa.AI",
}: WelcomeLoaderProps) => {
  const [phase, setPhase] = useState<1 | 2>(1);
  const [isEnding, setIsEnding] = useState(false);
  const switchMs = useMemo(() => Math.max(1200, Math.floor(durationMs * 0.52)), [durationMs]);
  const endMs = useMemo(() => Math.max(durationMs - 700, switchMs + 300), [durationMs, switchMs]);

  useEffect(() => {
    if (!onComplete) return;
    const phaseId = window.setTimeout(() => setPhase(2), switchMs);
    const endId = window.setTimeout(() => setIsEnding(true), endMs);
    const doneId = window.setTimeout(onComplete, durationMs);
    return () => {
      window.clearTimeout(phaseId);
      window.clearTimeout(endId);
      window.clearTimeout(doneId);
    };
  }, [durationMs, endMs, onComplete, switchMs]);

  return (
    <div className="fixed inset-0 z-[120] flex h-screen w-screen items-center justify-center overflow-hidden bg-transparent">
      <LightRays
        className="absolute inset-0 h-full w-full"
        raysOrigin="top-center"
        raysColor="#c9dcff"
        raysSpeed={1.1}
        lightSpread={0.45}
        rayLength={3}
        followMouse={false}
        mouseInfluence={0}
        noiseAmount={0.02}
        distortion={0.04}
        pulsating={false}
        fadeDistance={1}
        saturation={0.9}
      />
      <div className={`pointer-events-none absolute inset-0 z-[3] ${isEnding ? "loader-flash" : ""}`} />
      <div className={`absolute inset-0 z-[4] flex items-center justify-center px-6 text-center text-white [perspective:1200px] ${isEnding ? "loader-end-zoom" : ""}`}>
        {phase === 1 ? (
          <h1 className="loader-title flex items-center gap-3 font-alexandria text-3xl font-extrabold tracking-wide sm:gap-4 sm:text-5xl md:text-6xl">
            <Sparkles
              className="h-10 w-10 text-blue-300 drop-shadow-[0_0_14px_rgba(96,165,250,0.95)] sm:h-14 sm:w-14 md:h-16 md:w-16"
              style={{
                filter:
                  "drop-shadow(0 0 12px rgba(96,165,250,0.95)) drop-shadow(0 0 24px rgba(59,130,246,0.75)) drop-shadow(0 0 36px rgba(37,99,235,0.55))",
              }}
              aria-hidden
            />
            <span>{title}</span>
          </h1>
        ) : (
          <h1 className="loader-title font-alexandria text-3xl font-extrabold tracking-wide sm:text-5xl md:text-6xl">
            Build with Intelligence
          </h1>
        )}
      </div>
    </div>
  );
};

export default WelcomeLoader;
